import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Confetti from "react-confetti";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase/firebase";
import { query, where, onSnapshot, orderBy } from "firebase/firestore";
import { deleteDoc, updateDoc } from "firebase/firestore";
import { collection, addDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { requestNotificationPermission, listenForegroundMessages } from "./firebase/messaging";

import useSnackbar from "./hooks/useSnackbar";
import ScrollToTop from "./ScrollToTop";
import SnackbarProvider from "./providers/SnackbarProvider";
import AppLoader from "./MyComponents/AppLoader";
import UserOnboarding from "./MyComponents/UserOnboarding"; // next step
import Header from "./MyComponents/Header";
import BottomNav from "./MyComponents/BottomNav/BottomNav";
import { importantEvents } from "./config/events";
import Dates from "./MyComponents/Dates";
import Celebrations from "./MyComponents/Celebrations";
import EditDate from "./MyComponents/EditDate";
import ImportantDates from "./MyComponents/ImportantDates";
import CompletedDates from "./MyComponents/CompletedDates";
import Footer from "./MyComponents/Footer";
import { AddDate } from "./MyComponents/AddDate";
import CalendarView from "./MyComponents/CalendarView";
import About from "./MyComponents/About";
import "./App.css";

function AppInner() {
  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [dates, setDates] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [todayEvent, setTodayEvent] = useState(null);
  const [todayCelebration, setTodayCelebration] = useState(null);
  const [activeOverlay, setActiveOverlay] = useState(null);

  useEffect(() => {
    const initApp = async () => {
      const storedUserId = localStorage.getItem("userId");
      const storedName = localStorage.getItem("userName");

      if (!storedUserId) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", storedUserId);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          // User does not exist in Firebase
          localStorage.removeItem("userId");
          localStorage.removeItem("userName");
          setUserId(null);
        } else {
          const firebaseName = userSnap.data().name;

          if (firebaseName !== storedName) {
            // Name mismatch
            localStorage.clear();
            setUserId(null);
          } else {
            // Valid user
            setUserId(storedUserId);
          }
        }

      } catch (error) {
        console.error("User validation error:", error);
        setUserId(null);
      }

      setLoading(false);
    };

    initApp();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const q = query(
      collection(db, "dates"),
      where("userId", "==", userId),
      orderBy("eventDate", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedDates = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dateTime: doc.data().eventDate?.toDate().toISOString()
      }));

      setDates(fetchedDates);
    });

    return () => unsubscribe();
  }, [userId]);

  useEffect(() => {
    if (userId) {
      console.log("Initializing FCM for:", userId);

      requestNotificationPermission(userId);
      const unsubscribe = listenForegroundMessages();
      return () => unsubscribe?.();
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDate = today.getDate();

    const matchedEvent = importantEvents.find(event => {

      // Handle dynamic events
      if (event.dynamic && typeof event.getDate === "function") {
        const dynamicDate = event.getDate(currentYear);
        return (
          dynamicDate.getMonth() === currentMonth &&
          dynamicDate.getDate() === currentDate
        );
      }

      // Skip events without date
      if (!event.date) return false;

      const [month, day] = event.date.split("-").map(Number);

      return (
        month - 1 === currentMonth &&
        day === currentDate
      );
    });

    if (!matchedEvent) return;
    console.log("Matched event:", matchedEvent);

    const storageKey = `event-shown-${matchedEvent.id}-${today.getFullYear()}`;
    const alreadyShown = localStorage.getItem(storageKey);

    if (!alreadyShown) {
      setTodayEvent(matchedEvent);
    }

  }, [userId]);

  useEffect(() => {
    if (!userId || dates.length === 0) return;

    const today = new Date();
    const month = today.getMonth();
    const day = today.getDate();
    const year = today.getFullYear();

    const matchedRecurring = dates.find(item => {
      if (item.type !== "recurring") return false;

      const eventDate = new Date(item.dateTime);

      return (
        eventDate.getMonth() === month &&
        eventDate.getDate() === day
      );
    });

    if (!matchedRecurring) return;

    const storageKey = `celebration-shown-${matchedRecurring.id}-${year}`;
    const alreadyShown = localStorage.getItem(storageKey);

    if (!alreadyShown) {
      setTodayCelebration(matchedRecurring);
    }

  }, [dates, userId]);

  useEffect(() => {
    if (!userId) return;

    // If celebration exists → always show first
    if (todayCelebration) {
      setActiveOverlay("celebration");
      return;
    }

    // Only show important if NO celebration exists
    if (todayEvent) {
      setActiveOverlay("important");
    }

  }, [todayCelebration, todayEvent, userId]);


  function AppContent({
    dates,
    onDelete,
    togglePriority,
    updateDate,
    addDate,
    todayEvent,
  }) {
    const location = useLocation();

    const formattedToday = new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });

    return (
      <>
        <Header />
        <ScrollToTop />
        <Box component="main" sx={{ minHeight: "calc(100vh - 88px)" }}>
          <Routes>
            <Route
              path="/"
              element={
                <Dates
                  dates={dates}
                  onDelete={onDelete}
                  onPriority={togglePriority}
                />
              }
            />

            <Route
              path="/celebrations"
              element={
                <Celebrations
                  dates={dates}
                  onDelete={onDelete}
                  onPriority={togglePriority}
                />
              }
            />

            <Route
              path="/add"
              element={<AddDate addDate={addDate} />}
            />

            <Route
              path="/edit/:id"
              element={<EditDate dates={dates} onUpdate={updateDate} />}
            />

            <Route
              path="/favourite"
              element={
                <ImportantDates
                  dates={dates}
                  onDelete={onDelete}
                  onPriority={togglePriority}
                />
              }
            />

            <Route
              path="/completed"
              element={
                <CompletedDates
                  dates={dates}
                  onDelete={onDelete}
                  onPriority={togglePriority}
                />
              }
            />

            <Route
              path="/calendar"
              element={<CalendarView dates={dates} />}
            />

            <Route path="/about" element={<About />} />
          </Routes>
        </Box>

        {location.pathname === "/" && activeOverlay === "important" && todayEvent && (
          <div className="event-overlay">
            <div
              className="event-card-premium"
              style={{
                borderTop: `5px solid ${todayEvent.color}`,
                boxShadow: `0 40px 80px ${todayEvent.color}22`
              }}
            >
              <Chip
                label={todayEvent.category?.toUpperCase()}
                className="event-badge"
                sx={{
                  mb: 2,
                  bgcolor: `${todayEvent.color}15`,
                  color: todayEvent.color,
                  fontWeight: 800,
                }}
              />

              <h1 className="event-title">
                {todayEvent.title}
              </h1>

              <div className="event-date">
                {formattedToday}
              </div>

              <p className="event-description">
                {todayEvent.description}
              </p>

              <button
                className="event-close-btn"
                style={{
                  backgroundColor: todayEvent.color
                }}
                onClick={() => {
                  const today = new Date();
                  const storageKey = `event-shown-${todayEvent.id}-${today.getFullYear()}`;
                  localStorage.setItem(storageKey, "true");
                  setActiveOverlay(null);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {activeOverlay === "celebration" && (
          <Confetti numberOfPieces={300} recycle={false} />
        )}

        {activeOverlay === "celebration" && todayCelebration && (
          <div className="event-overlay">
            <div
              className="event-card-premium"
              style={{
                borderTop: "5px solid #7c3aed",
                boxShadow: "0 40px 80px rgba(124, 58, 237, 0.25)"
              }}
            >
              <Chip
                label="CELEBRATION"
                className="event-badge"
                sx={{
                  mb: 2,
                  bgcolor: "rgba(124, 58, 237, 0.15)",
                  color: "#7c3aed",
                  fontWeight: 800,
                }}
              />

              <h1 className="event-title">
                {todayCelebration.title}
              </h1>

              <div className="event-date">
                Today
              </div>

              <p className="event-description">
                Wishing you a wonderful celebration!
              </p>

              <button
                className="event-close-btn"
                style={{
                  backgroundColor: "#7c3aed"
                }}
                onClick={() => {
                  const today = new Date();
                  const storageKey = `celebration-shown-${todayCelebration.id}-${today.getFullYear()}`;
                  localStorage.setItem(storageKey, "true");

                  if (todayEvent) {
                    setActiveOverlay("important");
                  } else {
                    setActiveOverlay(null);
                  }
                }}
              >
                Celebrate
              </button>
            </div>
          </div>
        )}
        <BottomNav />
        <Footer />
        <Dialog
          open={Boolean(deleteTarget)}
          onClose={handleDeleteCancel}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 4,
              p: 1,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>Confirm deletion</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5}>
              <Typography variant="body1">
                This will permanently remove this date from your timeline.
              </Typography>
              {deleteTarget && (
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: 3,
                    bgcolor: "rgba(15, 23, 42, 0.04)",
                    border: "1px solid rgba(148, 163, 184, 0.18)",
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Selected item
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    {deleteTarget.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {deleteTarget.type === "recurring"
                      ? "This recurring celebration will be removed."
                      : "This scheduled date will be removed."}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
            <Button onClick={handleDeleteCancel} disabled={deleteLoading} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Add a new important date
  const addDate = async (newItem) => {
    try {
      await addDoc(collection(db, "dates"), {
        title: newItem.title,
        category: newItem.category,
        notes: newItem.notes,
        userId,
        priority: false,
        type: newItem.type,
        recurrence: newItem.recurrence,
        eventDate: Timestamp.fromDate(new Date(newItem.dateTime)),
        createdAt: serverTimestamp(),
      });

      console.log("Date saved to Firestore ✅");

    } catch (error) {
      console.error("Error saving date:", error);
      throw error;
    }
  };

  const updateDate = async (updatedItem) => {
    try {
      await updateDoc(doc(db, "dates", updatedItem.id), {
        title: updatedItem.title,
        category: updatedItem.category,
        notes: updatedItem.notes,
        eventDate: Timestamp.fromDate(new Date(updatedItem.dateTime)),
      });

      console.log("Date updated in Firestore ✅");
    } catch (error) {
      console.error("Update error:", error);
      throw error;
    }
  };

  // Delete a date
  const onDelete = (item) => {
    setDeleteTarget(item);
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    try {
      await deleteDoc(doc(db, "dates", deleteTarget.id));
      showSnackbar("Date deleted successfully!", "success");
      console.log("Date deleted from Firestore ✅");
      setDeleteTarget(null);
    } catch (error) {
      showSnackbar("Failed to delete date.", "error");
      console.error("Delete error:", error);
      throw error;
    } finally {
      setDeleteLoading(false);
    }
  };

  // Toggle priority (important)
  const togglePriority = async (item) => {
    try {
      await updateDoc(doc(db, "dates", item.id), {
        priority: !item.priority
      });
    } catch (error) {
      console.error("Priority update error:", error);
      throw error;
    }
  };

  if (loading) {
    return <AppLoader />;
  }

  if (!userId) {
    return <UserOnboarding onComplete={setUserId} />;
  }

  return (
    <Router>
      <AppContent
        dates={dates}
        onDelete={onDelete}
        togglePriority={togglePriority}
        updateDate={updateDate}
        addDate={addDate}
        todayEvent={todayEvent}
      />
    </Router>
  );
}

function App() {
  return (
    <SnackbarProvider>
      <AppInner />
    </SnackbarProvider>
  );
}

export default App;
